import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuthSePuder } from '@/contexts/AuthContext';
import { api, hasToken } from '@/lib/api';

interface SidebarContextValue {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  toggle: () => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

const PREFERENCE_KEY = 'sidebar.collapsed';

/**
 * Provider do estado da Sidebar — colapsada / expandida.
 *
 * **Backend-first (2026-05-17):** persiste em `user_ui_preferences` via
 * `/api/v1/users/me/ui-preferences/sidebar.collapsed`. Sem `localStorage`
 * (regra inviolável). Optimistic update: toggle atualiza UI imediato; PUT
 * roda em background. Se falhar, UI mantém estado novo — usuário toggleia
 * de novo se quiser.
 *
 * Inclui atalho global Ctrl+B (Windows/Linux) e Cmd+B (Mac) pra alternar.
 */
export function SidebarProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  // Sem `<AuthProvider>` em volta (montado sozinho): a regra de antes. Com ele: espera saber quem é.
  const auth = useAuthSePuder();
  const podeConsultar = auth === null ? true : !!auth.user && auth.user.tipo !== 'tecnico';

  // Estado otimista local — UI é instantânea, PUT roda async
  const [optimistic, setOptimistic] = useState<boolean | null>(null);

  const { data: backendValue } = useQuery({
    queryKey: ['v1', 'ui-preferences', PREFERENCE_KEY],
    queryFn: async () => {
      try {
        const r = await api.get<{ key: string; value: boolean }>(
          `/api/v1/users/me/ui-preferences/${PREFERENCE_KEY}`
        );
        return typeof r.value === 'boolean' ? r.value : false;
      } catch (err) {
        // 404 = sem preferência salva ainda → default false
        const status = (err as { status?: number })?.status;
        if (status === 404) return false;
        throw err;
      }
    },
    // 25/10/2026: só depois de saber QUEM é, e nunca para o técnico de campo — o aplicativo dele não tem menu lateral,
    // e o servidor recusa a preferência para ele (a recusa acendia o aviso vermelho "sem acesso" no celular).
    enabled: hasToken() && podeConsultar,
    staleTime: 60_000,
  });

  // Optimistic > backend > default
  const collapsed = optimistic ?? backendValue ?? false;

  const setCollapsed = useCallback(
    (next: boolean) => {
      setOptimistic(next);
      // Background PUT — sem await; UI não trava se backend tropeçar
      void (async () => {
        try {
          await api.put(`/api/v1/users/me/ui-preferences/${PREFERENCE_KEY}`, {
            value: next,
          });
          await qc.invalidateQueries({
            queryKey: ['v1', 'ui-preferences', PREFERENCE_KEY],
          });
        } catch {
          // Mantém UI no estado novo — usuário toggleia de novo se quiser
        }
      })();
    },
    [qc]
  );

  const toggle = useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed, setCollapsed]);

  /**
   * Atalho global: Ctrl/Cmd + \
   *
   * B-03 (HOTFIX-ROLAGEM): era Ctrl+B — a MESMA tecla de "negrito". Quem
   * escrevia numa observação e apertava Ctrl+B recolhia o menu sem querer, e
   * como a preferência é salva no servidor, ele voltava recolhido na visita
   * seguinte (foi a hipótese levantada quando o "abriu recolhida" do Matheus
   * não se reproduziu). Duas defesas agora:
   *   1. a tecla mudou para `\`, que não colide com formatação;
   *   2. o atalho é ignorado enquanto o foco está num campo de texto — dentro
   *      de input, textarea ou editor, o que o usuário digita é dele.
   */
  useEffect(() => {
    function digitandoEmCampo(alvo: EventTarget | null): boolean {
      const el = alvo as HTMLElement | null;
      if (!el || !el.tagName) return false;
      const tag = el.tagName.toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
    }
    function handleKey(e: KeyboardEvent) {
      const isModifier = e.ctrlKey || e.metaKey;
      if (!isModifier || e.key !== '\\') return;
      if (digitandoEmCampo(e.target)) return;
      e.preventDefault();
      toggle();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [toggle]);

  const value = useMemo(
    () => ({ collapsed, setCollapsed, toggle }),
    [collapsed, setCollapsed, toggle]
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSidebar(): SidebarContextValue {
  const ctx = useContext(SidebarContext);
  if (!ctx) {
    // Fallback seguro pra testes que não envolvem o provider
    return {
      collapsed: false,
      setCollapsed: () => {},
      toggle: () => {},
    };
  }
  return ctx;
}
